/**
 * Sectionly Feature Image Web Component
 * 实现特色图片 section 的交互功能
 */

(function() {
  'use strict';
  
  let swipers = {};
  let isInitialized = false;
  
  function createSwiper(sectionId) {
    const container = document.querySelector(`.sectionly-feature-image-swiper-${sectionId}`);
    if (!container) return null;
    
    if (swipers[sectionId]) {
      try {
        swipers[sectionId].destroy(true, true);
      } catch (e) {
        // Silent cleanup
      }
    }
    
    if (!window.Swiper) {
      return null;
    }
    
    try {
      swipers[sectionId] = new window.Swiper(`.sectionly-feature-image-swiper-${sectionId}`, {
        loop: true,
        pagination: {
          el: `.swiper-pagination-${sectionId}`,
          clickable: true
        },
        navigation: {
          nextEl: `.swiper-button-next-${sectionId}`,
          prevEl: `.swiper-button-prev-${sectionId}`
        },
        slidesPerView: 1,
        spaceBetween: 0,
        observer: true,
        observeParents: true
      });
      
      return swipers[sectionId];
    } catch (error) {
      return null;
    }
  }
  
  function initAllSwipers() {
    if (isInitialized) return;
    
    const sections = document.querySelectorAll('[data-section-id]');
    if (sections.length === 0) return;
    
    let allSwipersCreated = true;
    sections.forEach(section => {
      const sectionId = section.getAttribute('data-section-id');
      if (sectionId) {
        const swiper = createSwiper(sectionId);
        if (!swiper) {
          allSwipersCreated = false;
        }
      }
    });
    
    if (allSwipersCreated && sections.length > 0) {
      isInitialized = true;
    }
  }
  
  function forceInit() {
    isInitialized = false;
    initAllSwipers();
  }
  
  function waitForSwiper() {
    if (window.Swiper) {
      initAllSwipers();
    } else {
      setTimeout(() => {
        if (window.Swiper) {
          initAllSwipers();
        }
      }, 200);
    }
  }
  
  function setupEventListeners() {
    waitForSwiper();
    
    window.addEventListener('load', function() {
      setTimeout(forceInit, 100);
    });
    
    window.addEventListener('pageshow', function(event) {
      setTimeout(forceInit, 100);
    });
    
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function(event) {
        setTimeout(forceInit, 100);
      });
      
      document.addEventListener('shopify:section:unload', function(event) {
        const sectionId = event.target.querySelector('[data-section-id]')?.getAttribute('data-section-id');
        if (sectionId && swipers[sectionId]) {
          try {
            swipers[sectionId].destroy(true, true);
            delete swipers[sectionId];
          } catch (e) {
            // Silent cleanup
          }
        }
      });
    }
    
    const observer = new MutationObserver(function(mutations) {
      let hasChanges = false;
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              if (node.matches && node.matches('[data-section-id]')) {
                hasChanges = true;
              }
              if (node.querySelectorAll && node.querySelectorAll('[data-section-id]').length > 0) {
                hasChanges = true;
              }
            }
          });
        }
      });
      
      if (hasChanges) {
        setTimeout(forceInit, 100);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  setupEventListeners();
  
  window.sectionlyFeatureImageInit = forceInit;
})();

class SectionlyFeatureImageWebComponent extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('section-id');
    this.backgroundColor = this.getAttribute('background-color');
    this.section = null;
    this.observer = null;
    this.isInitialized = false;
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    } else {
      setTimeout(() => {
        this.init();
      }, 100);
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (this.isInitialized) return;
    
    this.section = document.getElementById(`section-${this.sectionId}`);
    if (!this.section) return;

    this.setupIntersectionObserver();
    this.setupImageLazyLoading();
    this.setupResponsiveHandling();
    this.applyCustomStyles();
    this.setupAccessibility();
    
    this.isInitialized = true;
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateIn();
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    if (this.section) {
      this.observer.observe(this.section);
    }
  }

  animateIn() {
    const elements = this.section.querySelectorAll(
      '.sectionly-feature-image-header, .sectionly-feature-image-description, .sectionly-feature-image-content'
    );

    elements.forEach((element, index) => {
      element.classList.add('sectionly-feature-image-fade-in');
      setTimeout(() => {
        element.classList.add('sectionly-feature-image-visible');
      }, index * 150);
    });
  }

  setupImageLazyLoading() {
    const images = this.section.querySelectorAll('.sectionly-feature-image-image');
    const placeholders = this.section.querySelectorAll('.sectionly-feature-image-placeholder');
    
    images.forEach(image => {
      if ('loading' in HTMLImageElement.prototype) {
        image.loading = 'lazy';
      }

      image.addEventListener('load', () => {
        image.classList.add('sectionly-feature-image-loaded');
      });

      image.addEventListener('error', () => {
        image.classList.add('sectionly-feature-image-error');
      });
    });
    
    placeholders.forEach(placeholder => {
      if (window.Shopify && window.Shopify.designMode) {
        placeholder.addEventListener('click', () => {
          this.dispatchEvent(new CustomEvent('sectionly-feature-image:placeholder-clicked', {
            detail: { sectionId: this.sectionId }
          }));
        });
      }
    });
  }

  setupResponsiveHandling() {
    this.handleResize = this.debounce(() => {
      this.updateResponsiveClasses();
    }, 250);

    window.addEventListener('resize', this.handleResize);
    this.updateResponsiveClasses();
  }

  updateResponsiveClasses() {
    const wrapper = this.section.querySelector('.sectionly-feature-image-wrapper');
    if (!wrapper) return;

    const width = window.innerWidth;
    
    wrapper.classList.remove(
      'sectionly-feature-image-mobile',
      'sectionly-feature-image-tablet',
      'sectionly-feature-image-desktop'
    );

    if (width <= 480) {
      wrapper.classList.add('sectionly-feature-image-mobile');
    } else if (width <= 768) {
      wrapper.classList.add('sectionly-feature-image-tablet');
    } else {
      wrapper.classList.add('sectionly-feature-image-desktop');
    }
  }

  applyCustomStyles() {
    const wrapper = this.section.querySelector('.sectionly-feature-image-wrapper');
    if (!wrapper) return;

    const textAlignment = this.getSettingValue('text_alignment', 'center');
    wrapper.setAttribute('data-text-align', textAlignment);

    const titleColor = this.getSettingValue('title_color', '#333333');
    const descriptionColor = this.getSettingValue('description_color', '#666666');
    const paddingTop = this.getSettingValue('padding_top', 60);
    const paddingBottom = this.getSettingValue('padding_bottom', 60);

    wrapper.style.setProperty('--title-color', titleColor);
    wrapper.style.setProperty('--description-color', descriptionColor);
    wrapper.style.setProperty('--text-alignment', textAlignment);
    wrapper.style.paddingTop = `${paddingTop}px`;
    wrapper.style.paddingBottom = `${paddingBottom}px`;
  }

  setupAccessibility() {
    const images = this.section.querySelectorAll('.sectionly-feature-image-image');
    const title = this.section.querySelector('.sectionly-feature-image-title');
    
    images.forEach(image => {
      if (!image.alt || image.alt.trim() === '') {
        image.alt = title ? title.textContent.trim() : 'Feature image';
      }
    });

    if (title) {
      title.setAttribute('role', 'heading');
      title.setAttribute('aria-level', '2');
    }
  }

  getSettingValue(settingName, defaultValue) {
    const wrapper = this.section.querySelector('.sectionly-feature-image-wrapper');
    if (wrapper && wrapper.dataset[settingName]) {
      return wrapper.dataset[settingName];
    }
    
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.settings) {
      return window.Shopify.theme.settings[settingName] || defaultValue;
    }
    
    return defaultValue;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  getSectionData() {
    return {
      sectionId: this.sectionId,
      backgroundColor: this.backgroundColor,
      isInitialized: this.isInitialized
    };
  }
}

if (!customElements.get('sectionly-feature-image-web-component')) {
  customElements.define('sectionly-feature-image-web-component', SectionlyFeatureImageWebComponent);
}

window.SectionlyFeatureImageWebComponent = SectionlyFeatureImageWebComponent; 